/**
 * TODO: 
 * - add onloadscreen
 */

import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import pathToModel from '../../assets/models/child.glb';
import texture_0 from '../../assets/img/textures/colors.jpg';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.startTime = Date.now();
        this.mouse = new THREE.Vector2(0, 0);
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/003052-007a1f-ff5969-ff99a3-ffdb9e');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[3];
        this.scene.fog = new THREE.Fog(this.colourArr[1], 0.1, 100);

        this.setupCamera();
        this.setupLight();
        this.setupGlobalUniforms();
        this.loadModels();
        this.setupControls();
        this.setupGeometry();
    }

    setupCamera() {
        let VIEW_ANGLE = 60;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.01;
        let FAR_PLANE = 100;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.z = 3;
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    setupLight() {
        let hemiLight = new THREE.HemisphereLight(this.colourArr[4], this.colourArr[0], 1);
        hemiLight.position.set(0, 0.5, -1);
        this.scene.add(hemiLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = false;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 80;
        this.controls.minDistance = 2;
        // this.controls.addEventListener('change', this.update.bind(this));
    }

    setupGlobalUniforms() {
        let loader = new THREE.TextureLoader();
        let texture = loader.load(texture_0);
        texture.minFilter = texture.magFilter = THREE.NearestFilter;
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        this.uniforms = {
            u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            u_time: { value: 0 },
            u_color0: { value: new THREE.Color(this.colourArr[0]) },
            u_texture_0: { value: texture }
        };
    }

    setupShaderOneMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec2 vUv; 
            
                void main()
                {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;
                uniform sampler2D u_texture_0;
                uniform vec3 u_color0;

                varying vec2 vUv;

                #define TIMESCALE 0.25 
                #define TILES 10
                #define COLOR 0.2, 1.6, 2.8

                void main()
                {
                    vec2 pos = vUv * (gl_FragCoord.xy/u_resolution);
                    pos.x *= u_resolution.x/u_resolution.y;

                    // vec4 noise = texture2D(u_texture_0, fract(pos * float(TILES)) / float(TILES));
                    // float p = 1.0 - mod(noise.r + noise.g + noise.b + u_time * float(TIMESCALE), 1.0);
                    // p = min(max(p * 3.0 - 1.8, 0.1), 2.0);
    
                    // vec2 r = mod(pos * float(TILES) + fract(u_time * 0.5), 1.0);
                    // r = vec2(pow(r.x - 0.5, 2.0), pow(r.y - 0.5, 2.0));
                    // p /= 1.0 - pow(min(1.0, 12.0 * dot(r, r)), 0.2);
    
                    // gl_FragColor = vec4(u_color0, 1.0) * p;

                    vec4 noise = texture2D(u_texture_0, floor(pos * float(TILES)) / float(TILES));
                    float p = 1.0 - mod(noise.r + noise.g + noise.b + u_time * float(TIMESCALE), 1.0);
                    p = min(max(p * 3.0 - 1.8, 0.1), 2.0);
 
                    vec2 r = mod(pos * float(TILES), 1.0);
                    r = vec2(pow(r.x - 0.5, 2.0), pow(r.y - 0.5, 2.0));
                    p *= 1.0 - pow(min(1.0, 12.0 * dot(r, r)), 2.0);
 
                    gl_FragColor = vec4(COLOR, 1.0) * p;
                }
            `
        });
        return material;
    }

    setupShaderTwoMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                precision mediump float;
                uniform float u_time;
                varying vec2 vUv; 
            
                void main()
                {
                    vUv = uv;
                    float modPos = abs(3.*sin(u_time*0.1));
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position*modPos, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;

                varying vec2 vUv;

                const float fluid_speed = 200.0;
                const float color_intensity = 0.5;

                vec2 random2(vec2 p) 
                {
                    return fract(sin(vec2(dot(p,vec2(127.1,311.7)),
                            dot(p,vec2(269.5,183.3))))*43758.5453);
                } 

                void main()
                {
                    vec2 pos = vUv * (gl_FragCoord.xy/u_resolution);
                    pos.x *= u_resolution.x/u_resolution.y;

                    vec2 i_pos= floor(pos); //tile coordinates stored as integer
                    vec2 f_pos = fract(pos); //pixel coordinates stored as float

                    float min_dist = 1.0;
                    for(int y=-1; y<=1; y++) 
                    {
                        for(int x=-1; x<=1; x++) 
                        {
                            vec2 neighbour = vec2(float(x), float(y));
                            //compute the random position of the points on each one of the 
                            //neighbours by adding the neighbour offset
                            vec2 point = random2(i_pos + neighbour);
                            //animate the points
                            point = 0.5 + 0.5*sin(u_time + 6.2831*point);
                            vec2 d = neighbour + point - f_pos; //difference between each pixel and random f.point 
                            float dist = length(d); //distance to the point
                            min_dist = min(min_dist, dist); //get the closest distance
                        }
                    }
                    //colour manipulation with helper functions
                    float r = length(pos) * 2.0;
                    float a = atan(pos.y, pos.x);

                    float f = cos(a*3.0);
                    // f = abs(cos(a*3.0));
                    // f = abs(cos(a*2.5))*0.5+0.3;
                    // f = abs(cos(a*12.0)*sin(a*3.0))*0.8+0.1;
                    // f = smoothstep(-0.5, 1.0, cos(a*10.0))*0.2+0.5;
                    f = abs(cos(a) * sin(a*3.0));


                    vec3 colour = vec3(min_dist);
                    //draw cell center
                    //colour.r = 0.3;
                    colour.r += step(sin(f), min_dist);
                    //colour.g += step(f*0.1, min_dist) * 0.3;
                    colour.g = 0.1;
                    colour.b += step(cos(f*0.2), min_dist) * 0.4;

                    gl_FragColor = vec4(vec3(colour.r, colour.g, colour.b), 1.0);
                }
            `
        });
        return material;
    }

    loadModels() {
        let material = this.setupShaderOneMaterial();
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, (gltf) => {
            this.root = gltf.scene;
            this.root.position.set(0, -5, -2);
            this.root.scale.set(0.01, 0.01, 0.01);
            this.root.traverse(node => {
                if (node.isMesh) {
                    node.material = material;
                }
            });
            this.scene.add(this.root);
        });
    }

    setupGeometry() {
        let sphereGeometry = new THREE.SphereGeometry(0.05, 30, 30);
        let material = this.setupShaderTwoMaterial();
        this.geoArray = [];
        for (let i = 0; i < 300; i++) {
            let mesh = new THREE.Mesh(sphereGeometry, material);
            mesh.position.x = Math.random() * 10 - 5;
            mesh.position.y = Math.random() * 10 - 5;
            mesh.position.z = Math.random() * 10 - 5;
            mesh.scale.x = mesh.scale.y = mesh.scale.z = Math.random() * 2 + 1;
            this.geoArray.push(mesh);
            this.scene.add(mesh);
        }
    }

    orbitCamera(rotSpeed) {
        let x = this.camera.position.x;
        let z = this.camera.position.z;

        this.camera.position.x = x * Math.cos(rotSpeed) + z * Math.sin(rotSpeed);
        this.camera.position.z = z * Math.cos(rotSpeed) - x * Math.sin(rotSpeed);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    updateSceneSubjects(time) {
        for (let i = 0, l = this.geoArray.length; i < l; i++) {
            let sphere = this.geoArray[i];
            sphere.position.x = 5 * Math.cos(time + i);
            sphere.position.y = 5 * Math.sin(time + i * 1.1);
        }
    }

    update() {
        if (this.disposed) return;
        //if(this.camera) this.orbitCamera(0.001);
        if (this.controls) this.controls.update();
        if (this.uniforms) this.uniforms['u_time'].value = 0.001 * (Date.now() - this.startTime);
        this.updateSceneSubjects(Date.now() * 0.0001);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onMouseMove(e) {
        this.mouse.x = (e.clientX - window.innerWidth / 2) / 100;
        this.mouse.y = (e.clientY - window.innerHeight / 2) / 100;
        this.camera.position.x += (this.mouse.x - this.camera.position.x) * .05;
        this.camera.position.y += (-this.mouse.y - this.camera.position.y) * .05;
    }

    dispose() {
        this.disposed = true;
        this.scene.dispose();
        this.renderer.dispose();
    }
}
